import { Box, Card, CircularProgress, Grid, Icon, IconButton, Switch, Autocomplete, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Form, Formik, useFormikContext } from "formik";
import FormField from "layouts/pages/users/new-user/components/FormField";
import PropTypes from "prop-types";
import { memo, useEffect, useState } from "react";
import { errorAlert, successAlert } from "utils/Functions";
import { get, postNoResponse } from "utils/WebService";
import * as Yup from "yup";

const forcaOptions = [
    { id: 0, label: "0 - Neutro/Nenhum", color: "#9e9e9e" },
    { id: 1, label: "1 - Fraco/Frio", color: "#03a9f4" },
    { id: 2, label: "2 - Médio/Morno", color: "#ff9800" },
    { id: 3, label: "3 - Forte/Quente", color: "#f44336" }
];

const StatusFichaCreateEdit = ({ id, closeCreateEdit }) => {
    const [isLoadingInfo, setIsLoadingInfo] = useState(false);
    const [equipesList, setEquipesList] = useState([]);

    useEffect(() => {
        get("/equipe/list-all").then((data) => {
            setEquipesList(data?.list || []);
        });
    }, []);

    const handleSumit = (values, actions) => {
        postNoResponse("/status-ficha/save", values)
            .then(() => {
                successAlert(!id ? "Criado com sucesso!" : "Alterações salvas!", closeCreateEdit);
            }).catch(() => {
                errorAlert(!id ? "Erro ao criar!" : "Erro ao salvar alterações.");
            })
            .finally(() => {
                actions.setSubmitting(false);
            });
    }

    return (
        <Card id="status-ficha-info">
            <MDBox display="flex" alignItems="center" p={3}>
                <IconButton onClick={closeCreateEdit} sx={{ p: 0, paddingRight: 1, paddingTop: 0.75 }}>
                    <MDTypography variant="h5"><Icon>arrow_back</Icon></MDTypography>
                </IconButton>
                <MDTypography variant="h6">{id ? "Editar" : "Adicionar"} Status</MDTypography>
            </MDBox>
            <Formik
                initialValues={{ id: null, ccNome: '', cvAtivo: true, cvIndGlobal: false, cvIndSistema: false, equipes: [], forca: 0 }}
                validationSchema={Yup.object().shape({
                    ccNome: Yup.string().required("Campo Obrigatório.")
                })}
                onSubmit={(values, actions) => {
                    actions.setSubmitting(true);
                    handleSumit(values, actions);
                }}>
                {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                    <Form autoComplete="off">
                        <FetchAndSetData id={id} setIsLoadingInfo={setIsLoadingInfo} closeCreateEdit={closeCreateEdit} />
                        <MDBox px={3}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={4}>
                                    <FormField
                                        loading={isLoadingInfo}
                                        type="text"
                                        label="Nome"
                                        name="ccNome"
                                        value={values.ccNome}
                                        placeholder="Informe o nome..."
                                        error={errors.ccNome?.length > 0 && touched.ccNome}
                                        inputProps={{ sx: { height: '30px' } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Autocomplete
                                        options={forcaOptions}
                                        getOptionLabel={(option) => option.label || ""}
                                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                                        value={forcaOptions.find(o => o.id === values.forca) || forcaOptions[0]}
                                        onChange={(e, value) => setFieldValue("forca", value ? value.id : 0)}
                                        disabled={isLoadingInfo}
                                        renderInput={(params) => (
                                            <TextField {...params} variant="standard" label="Nível do Termômetro" />
                                        )}
                                        renderOption={(props, option) => (
                                            <li {...props} style={{ display: 'flex', alignItems: 'center' }}>
                                                <div style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: option.color, marginRight: 8 }} />
                                                {option.label}
                                            </li>
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Autocomplete
                                        multiple
                                        options={equipesList}
                                        getOptionLabel={(option) => option.nome || ""}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        value={values.equipes}
                                        onChange={(e, value) => setFieldValue("equipes", value)}
                                        disabled={values.cvIndGlobal || values.cvIndSistema || isLoadingInfo}
                                        renderInput={(params) => (
                                            <TextField {...params} variant="standard" label="Equipes Permitidas" placeholder="Selecione as equipes..." />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} style={{ display: 'flex', alignItems: 'center' }}>
                                    {!isLoadingInfo && (
                                        <MDBox display="flex" alignItems="center" mr={3}>
                                            <Switch size="small" checked={values.cvAtivo || false} onChange={(event) => setFieldValue('cvAtivo', event.target.checked)} />
                                            <MDTypography variant="button" sx={{ fontSize: '11px', marginLeft: 0.5 }}>Ativo</MDTypography>
                                        </MDBox>
                                    )}
                                    {!isLoadingInfo && (
                                        <MDBox display="flex" alignItems="center" mr={3}>
                                            <Switch size="small" checked={values.cvIndGlobal || false} onChange={(event) => {
                                                setFieldValue('cvIndGlobal', event.target.checked);
                                                if (event.target.checked) setFieldValue('equipes', []);
                                            }} />
                                            <MDTypography variant="button" sx={{ fontSize: '11px', marginLeft: 0.5 }}>Global</MDTypography>
                                        </MDBox>
                                    )}
                                    {!isLoadingInfo && (
                                        <MDBox display="flex" alignItems="center">
                                            <Switch size="small" checked={values.cvIndSistema || false} onChange={(event) => {
                                                setFieldValue('cvIndSistema', event.target.checked);
                                                if (event.target.checked) setFieldValue('equipes', []);
                                            }} />
                                            <MDTypography variant="button" sx={{ fontSize: '11px', marginLeft: 0.5 }}>Sistema</MDTypography>
                                        </MDBox>
                                    )}
                                </Grid>
                            </Grid>
                        </MDBox>
                        <Box display="flex" alignItems="center" justifyContent="end" p={2}>
                            <MDButton disabled={isLoadingInfo} size="large" variant="contained" color="info" type="submit" endIcon={isSubmitting ? <CircularProgress color="inherit" size={20} /> : null}>
                                {!isSubmitting && (id ? "Salvar" : "Adicionar")}
                            </MDButton>
                        </Box>
                    </Form>
                )}
            </Formik>
        </Card>
    );
};

const FetchAndSetData = ({ id, setIsLoadingInfo, closeCreateEdit }) => {
    const { setFieldValue } = useFormikContext();
    useEffect(() => {
        if (id) {
            setIsLoadingInfo(true);
            get(`/status-ficha/by-id/${id}`)
                .then(data => {
                    setFieldValue('id', data.id || data.cvId);
                    setFieldValue('ccNome', data.ccNome);
                    setFieldValue('cvAtivo', data.cvAtivo);
                    setFieldValue('cvIndGlobal', data.cvIndGlobal);
                    setFieldValue('cvIndSistema', data.cvIndSistema);
                    setFieldValue('equipes', data.equipes || []);
                    setFieldValue('forca', data.forca || 0);
                    setIsLoadingInfo(false);
                }).catch(() => errorAlert("Não encontrado!", closeCreateEdit));
        }
    }, [id]);
    return null;
};

StatusFichaCreateEdit.propTypes = {
    id: PropTypes.any,
    closeCreateEdit: PropTypes.func.isRequired,
};

export default memo(StatusFichaCreateEdit);
